// Per-record financial summary shared by the Overview tab
// (FinancialSummary.tsx) and the dossier/DOCX exports (caseOverview.ts).
//
// ARCHITECTURE DECISION (post-Pinnacle round 4): cross-record arithmetic is
// REMOVED, not patched. No cross-record totals, no net line, no
// cross-record folding. Failure costs are asymmetric — a missing
// aggregation is harmless redundancy; a wrong displayed number destroys
// trust. The summary is one chronological group per source record that
// contains amount facts; the ONLY arithmetic rendered is an INTRA-record
// fold that verified exactly: when a record's component amounts (signed)
// sum exactly to a stated total amount in the SAME record, the components
// fold under that total. Anything else displays unfolded.
//
// amount_flow drives a per-line LABEL only. It never gates whether a fact
// appears — a flow misclassification ("38 days @ $42/day" tripping the
// rate guardrail on a $1,596 charge) must never hide a money fact.
//
// Pure functions, no React, no AI — unit-tested via node --test.

import type { CaseRecord, RecordFact } from "../types";
import { parseAmount } from "./factMeta";
import { dedupeFacts, normalizeForDedup, type DedupedFact } from "./dedupeFacts";

export type AmountFlow = "cost" | "recovery" | "offer" | "rate";

const FLOWS = new Set<AmountFlow>(["cost", "recovery", "offer", "rate"]);

// Effective flow of a fact for LABELLING: NULL/unknown → 'cost' (legacy
// rows, extracted before amount_flow existed, are out-of-pocket figures in
// practice). Never used to filter.
export function flowOf(f: RecordFact): AmountFlow {
  const raw = (f.amount_flow ?? "").toLowerCase();
  return FLOWS.has(raw as AmountFlow) ? (raw as AmountFlow) : "cost";
}

export const FLOW_LABELS: Record<AmountFlow, string> = {
  cost: "cost",
  recovery: "recovery",
  offer: "offer",
  rate: "rate",
};

export interface FinancialLine {
  fact: RecordFact;
  alsoIn: string[];
  // Sibling facts from the SAME record whose signed amounts sum exactly to
  // this line's amount. Visible on expand; shown indented in exports.
  components: DedupedFact[];
  // Same-record facts collapsed into this line: identical signed amount and
  // near-identical normalized title (the duplicate "$1,596 rental" pair).
  // Their quotes stay accessible. Never crosses records.
  duplicates: RecordFact[];
}

export interface RecordFinancialGroup {
  record_id: string;
  // Resolved from the record when available, else fact metadata.
  title: string;
  date: string | null;
  lines: FinancialLine[];
}

// Exact fold detection over signed integer cents — EXACT equality, a
// near-miss never folds. Shared house math: the financial summary's
// intra-record fold and the Costs-incurred card both use this (no
// widget-local arithmetic anywhere). Exhaustive subset search capped at
// FOLD_MAX_GROUP items; larger sets conservatively fold nothing. Candidate
// totals are tried from largest absolute value down — a stated total is
// normally the largest figure.
const FOLD_MAX_GROUP = 14;

export interface ExactFold {
  totalIndex: number;
  componentIndices: number[];
}

export function findExactFold(centsList: number[]): ExactFold | null {
  if (centsList.length < 3 || centsList.length > FOLD_MAX_GROUP + 1) return null;
  const order = centsList
    .map((c, i) => ({ c, i }))
    .sort((a, b) => Math.abs(b.c) - Math.abs(a.c));
  for (const { c: target, i: totalIndex } of order) {
    const restIdx = centsList.map((_, i) => i).filter((i) => i !== totalIndex);
    const n = restIdx.length;
    for (let mask = 1; mask < 1 << n; mask++) {
      let bits = 0;
      let sum = 0;
      for (let i = 0; i < n; i++) {
        if (mask & (1 << i)) {
          bits++;
          sum += centsList[restIdx[i]!]!;
        }
      }
      if (bits < 2 || sum !== target) continue;
      return {
        totalIndex,
        componentIndices: restIdx.filter((_, i) => mask & (1 << i)),
      };
    }
  }
  return null;
}

function cents(f: RecordFact): number {
  return Math.round(parseAmount(f.amount) * 100);
}

function foldGroup(lines: FinancialLine[]): FinancialLine[] {
  const fold = findExactFold(lines.map((l) => cents(l.fact)));
  if (!fold) return lines;
  const total = lines[fold.totalIndex]!;
  const componentSet = new Set(fold.componentIndices);
  const folded = lines.filter((_, i) => componentSet.has(i));
  const keep = lines.filter((_, i) => i !== fold.totalIndex && !componentSet.has(i));
  return [
    ...keep,
    {
      ...total,
      components: [
        ...total.components,
        ...folded.map((l) => ({ fact: l.fact, alsoIn: l.alsoIn })),
      ],
    },
  ];
}

// Small edit-distance for the near-identical-title check (short strings).
function editDistance(a: string, b: string): number {
  if (Math.abs(a.length - b.length) > 4) return 99;
  const dp = Array.from({ length: a.length + 1 }, (_, i) => i);
  for (let j = 1; j <= b.length; j++) {
    let prev = dp[0]!;
    dp[0] = j;
    for (let i = 1; i <= a.length; i++) {
      const tmp = dp[i]!;
      dp[i] = Math.min(dp[i]! + 1, dp[i - 1]! + 1, prev + (a[i - 1] === b[j - 1] ? 0 : 1));
      prev = tmp;
    }
  }
  return dp[a.length]!;
}

function nearIdenticalTitles(a: RecordFact, b: RecordFact): boolean {
  const na = normalizeForDedup(a.summary);
  const nb = normalizeForDedup(b.summary);
  if (na === nb) return true;
  if (na.includes(nb) || nb.includes(na)) return true;
  return editDistance(na, nb) <= 3;
}

// Collapse same-record duplicates: identical signed amount + near-identical
// normalized title. The collapsed line keeps every source quote accessible.
// Same-record only — never cross-record. Exported: the expense statement
// applies the same rule to its row pool before duplicate-flagging.
export function collapseSameRecordDuplicates(lines: FinancialLine[]): FinancialLine[] {
  const out: FinancialLine[] = [];
  for (const line of lines) {
    const prior = out.find(
      (p) =>
        cents(p.fact) === cents(line.fact) &&
        nearIdenticalTitles(p.fact, line.fact),
    );
    if (prior) prior.duplicates.push(line.fact, ...line.duplicates);
    else out.push(line);
  }
  return out;
}

// Build the chronological per-record groups. The fold's CANDIDATE POOL is
// ALL amount facts of the record regardless of fact_category — category
// routing must not blind the arithmetic (the Settlement fold needs the
// 'valuation' ACV and 'coverage_term' deductible facts). Display placement:
// the group's visible lines are fold totals (any category, the arithmetic
// claim) plus unconsumed case_event amounts; facts of other categories
// still render in their own sections — appearing both places is
// corroboration, not duplication. Flow, sign, and classification never
// exclude a fact from the pool.
export function buildFinancialGroups(
  facts: RecordFact[],
  recordById: Map<string, CaseRecord>,
): RecordFinancialGroup[] {
  const amounts = facts.filter(
    (f) => f.fact_type === "amount" && parseAmount(f.amount) !== 0,
  );
  const deduped = dedupeFacts(amounts, recordById);

  const byRecord = new Map<string, FinancialLine[]>();
  const order: string[] = [];
  for (const d of deduped) {
    const id = d.fact.record_id;
    let g = byRecord.get(id);
    if (!g) {
      byRecord.set(id, (g = []));
      order.push(id);
    }
    g.push({ fact: d.fact, alsoIn: d.alsoIn, components: [], duplicates: [] });
  }

  const isCaseEvent = (f: RecordFact) => (f.fact_category ?? "case_event") === "case_event";

  const groups: RecordFinancialGroup[] = order.map((id) => {
    const rec = recordById.get(id);
    const folded = foldGroup(collapseSameRecordDuplicates(byRecord.get(id)!));
    // Visible lines: fold totals always (the arithmetic claim), other lines
    // only when they belong to the financial summary's own section.
    const lines = folded.filter((l) => l.components.length > 0 || isCaseEvent(l.fact));
    return {
      record_id: id,
      title: rec?.subject?.trim() || "Record",
      date: rec?.record_date?.slice(0, 10) ?? lines[0]?.fact.date ?? null,
      lines,
    };
  });

  return groups
    .filter((g) => g.lines.length > 0)
    .sort((a, b) => {
      const da = a.date ?? "9999";
      const db = b.date ?? "9999";
      return da < db ? -1 : da > db ? 1 : a.title.localeCompare(b.title);
    });
}
